import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import type { SpreadKind } from '../../api/client'

interface KindCard {
  kind: SpreadKind
  titleKey: 'tarot.k_3card' | 'tarot.k_5card' | 'tarot.k_celtic'
  descKey: 'tarot.k_3card_desc' | 'tarot.k_5card_desc' | 'tarot.k_celtic_desc'
  /** How many fanned card backs to show as the visual hint. */
  cards: number
  premium: boolean
}

const KINDS: KindCard[] = [
  { kind: '3card', titleKey: 'tarot.k_3card', descKey: 'tarot.k_3card_desc', cards: 3, premium: false },
  { kind: '5card_relationship', titleKey: 'tarot.k_5card', descKey: 'tarot.k_5card_desc', cards: 5, premium: true },
  { kind: 'celtic_cross', titleKey: 'tarot.k_celtic', descKey: 'tarot.k_celtic_desc', cards: 10, premium: true },
]

export function TarotIndexRoute() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-5 pb-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl text-sage-800">{t('tabs.tarot')}</h1>
        <p className="text-sm text-sage-600">{t('tarot.intro')}</p>
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-xs uppercase tracking-wider text-sage-500">{t('tarot.section_spreads')}</h2>
        <ul className="flex flex-col gap-3">
          {KINDS.map((k) => (
            <li key={k.kind}>
              <Link
                to={`/tarot/${k.kind}`}
                className="flex items-center gap-4 rounded-3xl bg-white/70 p-4 ring-1 ring-sage-200/60 transition hover:bg-white hover:shadow-sm active:scale-[0.99]"
              >
                <CardStack count={k.cards} />
                <div className="min-w-0 flex-1">
                  <h3 className="font-serif text-base text-sage-800">{t(k.titleKey)}</h3>
                  <p className="text-sm text-sage-600">{t(k.descKey)}</p>
                </div>
                {k.premium && (
                  <span className="shrink-0 rounded-full bg-sage-100 px-2 py-0.5 text-[11px] text-sage-700">
                    {t('app.premium')}
                  </span>
                )}
                <ChevronRight className="h-4 w-4 shrink-0 text-sage-400" strokeWidth={1.8} aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-3 border-t border-sage-200/60 pt-5">
        <h2 className="text-xs uppercase tracking-wider text-sage-500">{t('tarot.section_explore')}</h2>
        <Link
          to="/tarot/cards"
          className="flex items-center gap-4 rounded-3xl bg-white/70 p-4 ring-1 ring-sage-200/60 transition hover:bg-white hover:shadow-sm active:scale-[0.99]"
        >
          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
            <img
              src="/cards/card_back.webp"
              alt=""
              aria-hidden="true"
              draggable={false}
              className="h-11 w-7 rounded-md border border-sage-300/40 object-cover shadow-sm"
              style={{ transform: 'rotate(-8deg)' }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-base text-sage-800">{t('tarot.explore_deck_title')}</h3>
            <p className="text-sm text-sage-600">{t('tarot.explore_deck_desc')}</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-sage-400" strokeWidth={1.8} aria-hidden />
        </Link>

        <Link
          to="/tarot/history"
          className="flex items-center gap-4 rounded-3xl bg-white/70 p-4 ring-1 ring-sage-200/60 transition hover:bg-white hover:shadow-sm active:scale-[0.99]"
        >
          <img
            src="/icons/tarot-history.png"
            alt=""
            aria-hidden="true"
            className="h-12 w-12 shrink-0 select-none"
            draggable={false}
          />
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-base text-sage-800">{t('tarot.history_title')}</h3>
            <p className="text-sm text-sage-600">{t('tarot.history_desc')}</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-sage-400" strokeWidth={1.8} aria-hidden />
        </Link>
      </section>
    </div>
  )
}

/**
 * Visual preview of how many cards a spread uses — a tight fan/stack of card
 * backs. We render N copies of card_back.webp with progressive negative
 * margin + slight rotation so they read as "many cards" instantly. Used in
 * place of an emoji in the spread-kind list.
 */
function CardStack({ count }: { count: number }) {
  const visible = Math.min(count, 5)
  const tilts = [-12, -6, 0, 6, 12].slice(0, visible)
  return (
    <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
      {tilts.map((tilt, i) => (
        <img
          key={i}
          src="/cards/card_back.webp"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="absolute h-11 w-7 rounded-md border border-sage-300/40 object-cover shadow-sm"
          style={{
            transform: `translateX(${(i - (visible - 1) / 2) * 4}px) rotate(${tilt}deg)`,
            zIndex: i,
          }}
        />
      ))}
      {count > visible && (
        <span className="absolute -right-1 -top-1 z-20 rounded-full bg-sage-700 px-1.5 py-0.5 text-[9px] font-medium text-cream">
          ×{count}
        </span>
      )}
    </div>
  )
}
